import { supabase } from './supabase';

const API_BASE = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? '' : 'https://app.fahryo.de');

/**
 * Get a fresh admin access token. 
 * If the current session is expired, this will auto-refresh it.
 * If refresh fails, redirects to login.
 */
export async function getAdminToken(): Promise<string> {
  // First try the current session
  const { data: { session } } = await supabase.auth.getSession();
  
  if (session?.access_token) {
    // Check if token is about to expire (within 60 seconds)
    const expiresAt = session.expires_at ?? 0;
    const now = Math.floor(Date.now() / 1000);
    
    if (expiresAt - now > 60) {
      // Token is still fresh
      return session.access_token;
    }
  }
  
  // Token expired or about to expire → refresh
  const { data: { session: refreshed }, error } = await supabase.auth.refreshSession();
  
  if (error || !refreshed?.access_token) {
    // Refresh failed → force re-login
    console.warn('[Admin] Session expired and refresh failed — redirecting to login');
    await supabase.auth.signOut();
    window.location.reload();
    throw new Error('Session expired');
  }
  
  return refreshed.access_token;
}

/**
 * Fetch wrapper that auto-attaches the admin token and handles 401 errors.
 * If a 401 is received, it tries to refresh the token once and retry.
 */
export async function adminFetch(path: string, opts: RequestInit = {}): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  
  const token = await getAdminToken();
  const resp = await fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...opts.headers,
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (resp.status === 401) {
    // Token was valid locally but server rejected it → try refresh once
    console.warn('[Admin] Got 401 — attempting token refresh...');
    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (error || !session?.access_token) {
      await supabase.auth.signOut();
      window.location.reload();
      throw new Error('Session expired');
    }
    
    // Retry with refreshed token
    return fetch(url, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...opts.headers,
        'Authorization': `Bearer ${session.access_token}`,
      },
    });
  }
  
  return resp;
}

export { API_BASE };
